/**
 * @module builder
 */
import type { OptionsDocument, QuoteDocument } from 'paintscout'

import type { RenderableItem } from '../items'
import { getItems } from '../items'
import type { QuoteItemView } from '../types'
import { getQuoteOptions } from '../util'
import { applyWorkOrderCardOrder, getWorkOrderCardLayouts } from './work-order-layout'
import type { WorkOrderLayoutView } from './work-order-layout'

/**
 * Hidden work-order cards, plus the combined count. `area`/`substrate` hold the cards hidden in that
 * one view (`quote.workOrderLayout[view].cards[key].hidden`); `lineItem` holds the line items hidden
 * by the estimate's "Hidden on Work Order" checkbox (`lineItem.hideOnWorkOrder`), which hides them
 * from both views at once and so is listed on its own rather than under each view (ENG-1930).
 */
export interface WorkOrderHiddenItems {
  area: RenderableItem[]
  substrate: RenderableItem[]
  lineItem: RenderableItem[]
  total: number
}

/**
 * Build the full set of work-order cards for one view, in the view's saved card order — the same
 * item set the work-order grids render (bid + added-options + additional all share the view's layout
 * bucket), so hidden filtering and keys line up exactly.
 */
export function getWorkOrderViewItems(args: {
  quote: QuoteDocument
  options: OptionsDocument
  view: QuoteItemView
  showValues?: boolean
  lang?: string
  /**
   * Include line items the estimate's "Hidden on Work Order" checkbox hides. Off by default — the
   * grids must not render them; only the "Hidden items" panel asks for them.
   */
  includeHiddenLineItems?: boolean
}): RenderableItem[] {
  const { quote, options, view, showValues, lang, includeHiddenLineItems } = args
  const quoteOptions = getQuoteOptions({ options, quote })
  const common = {
    quote,
    view,
    consumer: 'crew' as const,
    options,
    overrideOptions: { showQuantities: true, ...(includeHiddenLineItems ? { showHiddenItems: true } : {}) },
    showLineItems: true,
    showValues,
    hideDimensions: quoteOptions.hideWorkOrderDimensions,
    lang
  }
  const items = [
    ...getItems({ ...common, section: 'bid' }),
    ...getItems({ ...common, section: 'added-options' }),
    ...getItems({ ...common, section: 'additional' })
  ]
  return applyWorkOrderCardOrder(items, quote.workOrderLayout?.[view]?.order)
}

/**
 * Collect the hidden work-order cards: the ones hidden per view (per-card `hidden` flags on
 * `quote.workOrderLayout[view].cards`), and the line items the estimate's "Hidden on Work Order"
 * checkbox hides (`lineItem.hideOnWorkOrder`). Powers the top-bar "Hidden items (N)" count and the
 * unified "Hidden items" section.
 */
export function getWorkOrderHiddenItems(args: {
  quote: QuoteDocument
  options: OptionsDocument
  lang?: string
}): WorkOrderHiddenItems {
  const { quote, options, lang } = args
  const quoteOptions = getQuoteOptions({ options, quote })
  const empty: WorkOrderHiddenItems = { area: [], substrate: [], lineItem: [], total: 0 }

  // Nothing is hidden either way — skip building both views' card lists (this runs on every render
  // of the work order).
  const hasHiddenLineItems = Object.keys(quote.lineItems ?? {}).some((key) => quote.lineItems[key]?.hideOnWorkOrder)
  const hasCardLayouts = (['area', 'substrate'] as WorkOrderLayoutView[]).some(
    (view) => Object.keys(getWorkOrderCardLayouts(quote, view)).length > 0
  )
  if (!hasHiddenLineItems && !hasCardLayouts) {
    return empty
  }

  // `hideOnWorkOrder` hides a line item from both views, so it's split out of the per-view buckets
  // and de-duplicated below rather than being listed once under Areas and again under Surfaces.
  const isHiddenLineItem = (item: RenderableItem) =>
    item.type === 'line_item' && !!quote.lineItems?.[item.key]?.hideOnWorkOrder

  const collect = (view: WorkOrderLayoutView, showValues?: boolean) => {
    const cards = getWorkOrderCardLayouts(quote, view)
    const items = getWorkOrderViewItems({
      quote,
      options,
      view,
      showValues,
      lang,
      includeHiddenLineItems: hasHiddenLineItems
    })
    return {
      lineItems: items.filter(isHiddenLineItem),
      viewItems: items.filter((item) => !isHiddenLineItem(item) && cards[item.key]?.hidden)
    }
  }

  const area = collect('area', quoteOptions.showWorkOrderAreaValues)
  const substrate = collect('substrate', quoteOptions.showWorkOrderSubstrateValues)

  const seen = new Set<string>()
  const lineItem = [...area.lineItems, ...substrate.lineItems].filter((item) => {
    if (seen.has(item.key)) {
      return false
    }
    seen.add(item.key)
    return true
  })

  return {
    area: area.viewItems,
    substrate: substrate.viewItems,
    lineItem,
    total: area.viewItems.length + substrate.viewItems.length + lineItem.length
  }
}
